import React, { useContext, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { AtlassianIcon } from '@atlaskit/logo';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import { token } from '@atlaskit/tokens';

import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
} from '../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../hooks/useSnippetAnalytics.ts';
import { useInsertSnippet } from '../hooks/useInsertSnippet.ts';
import {
	ATLASSIAN_ACCOUNT_ID,
	type Snippet,
	type SnippetOwner,
	SnippetScope,
	type UserSnippetOwner,
} from '../snippetTypes.ts';
import { SnippetPreview } from '../preview/SnippetPreview.tsx';
import { useSnippetsActions } from '../store/snippetsStore.ts';

import { SnippetPanelActions } from './SnippetPanelActions.tsx';
import { useSnippetPopupPreview } from './SnippetPopupPreviewContext.tsx';
import { SnippetPreviewSkeleton } from './skeleton/SnippetPreviewSkeleton.tsx';
import { SnippetPanelFavoriteButton } from './SnippetPanelFavoriteButton.tsx';

const SNIPPET_PREVIEW_WIDTH = 140;
const SNIPPET_PREVIEW_HEIGHT = 80;
const EDITOR_PRE_SCALE_WIDTH = 800;
const PREVIEW_PADDING_BLOCK = token('space.075');
const PREVIEW_PADDING_INLINE = token('space.100');

const styles = cssMap({
	cardWrapper: {
		position: 'relative',
		display: 'flex',
		width: '100%',
		height: '100px',
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	cardWrapperActive: {
		backgroundColor: token('elevation.surface.hovered'),
	},
	cardButtonOverlay: {
		position: 'absolute',
		top: token('space.0'),
		right: token('space.0'),
		bottom: token('space.0'),
		left: token('space.0'),
		backgroundColor: 'transparent',
		textAlign: 'left',
	},
	cardContentLayer: {
		position: 'relative',
		width: '100%',
		zIndex: 1,
		pointerEvents: 'none',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
	actionsContainer: {
		pointerEvents: 'auto',
		flexShrink: 0,
	},
	preview: {
		width: `${SNIPPET_PREVIEW_WIDTH}px`,
		minWidth: `${SNIPPET_PREVIEW_WIDTH}px`,
		height: `${SNIPPET_PREVIEW_HEIGHT}px`,
		borderRadius: token('radius.large'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		overflow: 'hidden',
		backgroundColor: token('elevation.surface'),
		paddingBlock: PREVIEW_PADDING_BLOCK,
		paddingInline: PREVIEW_PADDING_INLINE,
	},
	titleContainer: {
		flex: 1,
		textAlign: 'left',
	},
	titleContainerFilled: {
		width: '100%',
	},
});

export const messages = defineMessages({
	snippetUses: {
		id: 'snippets.panel.item.uses',
		defaultMessage: '{formattedCount} {count, plural, one {Use} other {Uses}}',
		description:
			'Shows the number of uses for a specific Block Template (singular "use" for 1, plural "uses" otherwise)',
	},
	snippetCardAriaLabel: {
		id: 'snippets.panel.item.card.aria-label',
		defaultMessage: 'Block Template: {name}',
		description: 'Label for block template card button',
	},
	snippetPreview: {
		id: 'snippets.panel.item.preview',
		defaultMessage: 'Preview',
		description: 'Text shown in the preview area of a block template card',
	},
});

type SnippetPanelItemProps = {
	snippet: Snippet;
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

export const SnippetPanelItem = ({
	snippet,
	contentId,
	spaceId,
	spaceKey,
}: SnippetPanelItemProps) => {
	const { userId } = useSessionData();
	const { editorAPI } = useContext(EditorContextPanelContext);
	const intl = useIntl();

	const [isHovered, setIsHovered] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const insertSnippet = useInsertSnippet(editorAPI);
	const snippetsActions = useSnippetsActions();
	const { fireButtonClickedAnalytic, fireFailedAnalytic } = useSnippetAnalytics(
		ANALYTICS_ORIGIN.SNIPPET_PANEL,
	);
	const { showPreview } = useSnippetPopupPreview();

	const handleMouseEnter = (event: React.MouseEvent) => {
		setIsHovered(true);
		showPreview(snippet, event.currentTarget.getBoundingClientRect());
	};
	const handleMouseLeave = () => setIsHovered(false);
	const handleMenuOpenChange = (isOpen: boolean) => setIsMenuOpen(isOpen);
	const onClick = () => {
		const success = insertSnippet(snippet);
		if (success) {
			snippetsActions.recordSnippetInsert(snippet.snippetId);
			fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.INSERT_SNIPPET, {
				userId: userId ?? undefined,
				scope: snippet.scope,
				snippetId: snippet.snippetId,
				isShared: snippet.isShared,
				timestamp: Date.now(),
			});
		} else {
			fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.INSERT_SNIPPET, {
				userId: userId ?? undefined,
				scope: snippet.scope,
				snippetId: snippet.snippetId,
				isShared: snippet.isShared,
				timestamp: Date.now(),
			});
		}
	};

	const showActions = isHovered || snippet.isFavorite || isMenuOpen;
	const showPanelActions = isHovered || isMenuOpen;

	return (
		<Box
			xcss={cx(styles.cardWrapper, isMenuOpen && styles.cardWrapperActive)}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<Pressable
				xcss={styles.cardButtonOverlay}
				onClick={onClick}
				testId={`snippet-card-button-${snippet.snippetId}`}
				aria-label={intl.formatMessage(messages.snippetCardAriaLabel, { name: snippet.name })}
			/>
			<Inline
				xcss={styles.cardContentLayer}
				alignInline="start"
				alignBlock="center"
				space="space.150"
			>
				<SnippetPreviewContainer snippet={snippet} />
				<SnippetCardContent
					snippet={snippet}
					isHovered={isHovered}
					showActions={showActions}
					showPanelActions={showPanelActions}
					onMenuOpenChange={handleMenuOpenChange}
					contentId={contentId}
					spaceId={spaceId}
					spaceKey={spaceKey}
				/>
			</Inline>
		</Box>
	);
};

const SnippetPreviewContainer = ({ snippet }: { snippet: Snippet }) => (
	<Box xcss={styles.preview}>
		<SnippetPreview
			snippet={snippet}
			dimensions={{
				width: `calc(${SNIPPET_PREVIEW_WIDTH}px - (${PREVIEW_PADDING_INLINE} * 2))`,
				maxHeight: `calc(${SNIPPET_PREVIEW_HEIGHT}px - (${PREVIEW_PADDING_BLOCK} * 2))`,
			}}
			renderWidth={EDITOR_PRE_SCALE_WIDTH}
			loadingComponent={<SnippetPreviewSkeleton />}
		/>
	</Box>
);

const formatInsertCount = (count: number): string => {
	if (count >= 1000) {
		const thousands = count / 1000;
		const isWholeNumber = thousands % 1 === 0;
		const usageValue = isWholeNumber ? thousands.toFixed(0) : thousands.toFixed(1);
		return `${usageValue}K`;
	}
	return String(count);
};

const CreatedByAvatar = ({ snippetOwner }: { snippetOwner: SnippetOwner }) => {
	const shouldUseAtlassianIcon = snippetOwner.accountId.toLowerCase() === ATLASSIAN_ACCOUNT_ID;
	if (shouldUseAtlassianIcon) {
		return <AtlassianIcon appearance="brand" size="xsmall" />;
	}

	return (
		<Avatar
			appearance="circle"
			size="xsmall"
			src={(snippetOwner as UserSnippetOwner).avatarUrl}
			name={snippetOwner.displayName}
		/>
	);
};

type SnippetCardContentProps = {
	snippet: Snippet;
	isHovered: boolean;
	showActions: boolean;
	showPanelActions: boolean;
	onMenuOpenChange: (isOpen: boolean) => void;
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

const SnippetCardContent = ({
	snippet,
	isHovered,
	showActions,
	showPanelActions,
	onMenuOpenChange,
	contentId,
	spaceId,
	spaceKey,
}: SnippetCardContentProps) => {
	const intl = useIntl();
	const showUsageCount =
		(snippet.scope === SnippetScope.SITE || snippet.scope === SnippetScope.GLOBAL) &&
		snippet.insertCount !== undefined;
	const shouldFillContainer = !isHovered;

	return (
		<Stack alignInline="start" alignBlock="start" space="space.075" grow="fill">
			<Inline alignBlock="center" space="space.050" grow="fill">
				<Box xcss={cx(styles.titleContainer, shouldFillContainer && styles.titleContainerFilled)}>
					<Heading size="xsmall">
						<Text maxLines={3} weight="bold">
							{snippet.name}
						</Text>
					</Heading>
				</Box>
				{showActions && (
					<Inline xcss={styles.actionsContainer} alignBlock="center" space="space.050">
						{showPanelActions && (
							<SnippetPanelActions
								snippet={snippet}
								isHovered={isHovered}
								onMenuOpenChange={onMenuOpenChange}
								contentId={contentId}
								spaceId={spaceId}
								spaceKey={spaceKey}
							/>
						)}
						<SnippetPanelFavoriteButton snippet={snippet} isHovered={isHovered} />
					</Inline>
				)}
			</Inline>
			<Inline alignBlock="center" separator="•" space="space.075">
				<Inline alignInline="center" alignBlock="center" space="space.050">
					<CreatedByAvatar snippetOwner={snippet.createdBy} />
					<Text color="color.text.subtlest" size="small" maxLines={1}>
						{snippet.createdBy.displayName}
					</Text>
				</Inline>
				{showUsageCount && (
					<Text color="color.text.subtlest" size="small">
						{intl.formatMessage(messages.snippetUses, {
							formattedCount: formatInsertCount(snippet.insertCount!),
							count: snippet.insertCount!,
						})}
					</Text>
				)}
			</Inline>
		</Stack>
	);
};
